/**
 * 날짜·시각을 받는 칸 — 보이는 글은 「yyyy-mm-dd」·「yyyy-mm-dd HH:mm:ss」, 옆 단추로 달력을 연다.
 *
 * 사장님 2026-10-01 「시간 표기는 yyyy-mm-dd HH:mm:ss 이해됬니」 — 보이는 모양을 맞추라는 말씀이다. 브라우저 날짜
 * 칸(type=date·datetime-local)은 브라우저 언어에 따라 「2026. 10. 01. 오후 02:20」처럼 보여 칸 자체는 글 칸으로 둔다.
 * 글로 쳐도 된다. 시간대는 관리자 컴퓨터 시간이다(format.ts).
 *
 * 달력: 사장님 2026-10-02 「1」 — 「관리자 웹이 직접 그리는 영어 달력으로 바꿉니다. 어느 컴퓨터에서나 영어로 나옵니다.」 —
 * 단추는 웹이 그리는 영어 달력(Calendar.tsx)을 연다. 고르면 글 칸에 이 모양으로 들어간다. 그 전의 브라우저 고르개
 * (숨은 브라우저 날짜 칸의 `showPicker` — 컴퓨터 언어를 따름)는 쓰지 않는다.
 */
import { useCallback, useId, useRef, useState } from 'react'
import { DATE_FORMAT, DATE_TIME_FORMAT } from '@/format'
import { Calendar } from './Calendar'
import { INPUT } from './ui'

function CalendarIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="4.5" width="14" height="12.5" rx="1.5" />
      <path d="M3 8.5h14M7 2.5v4M13 2.5v4" />
    </svg>
  )
}

/**
 * 달력 단추 — 누르면 영어 달력(Calendar)을 열고 닫는다. 고른 값(「yyyy-mm-dd」 또는 「yyyy-mm-dd HH:mm:ss」, 「Clear」면 빈 글)을
 * `onPick` 으로. `name` 은 단추 이름(「{name} calendar」 — 화면 읽기·시험이 칸마다 가른다), `value` 는 지금 칸의 글(그 날에서
 * 연다), `clearable` 은 「Clear」를 둘지(빈 값을 받는 칸).
 */
export function CalendarButton({ kind, name, value, onPick, clearable = true }: {
  kind: 'date' | 'datetime'
  name: string
  value: string
  onPick: (text: string) => void
  clearable?: boolean
}) {
  const [open, setOpen] = useState(false)
  const button = useRef<HTMLButtonElement>(null)
  const close = useCallback((refocus: boolean) => {
    setOpen(false)
    if (refocus) button.current?.focus()
  }, [])
  return (
    <span className="relative inline-flex">
      <button ref={button} type="button" aria-label={`${name} calendar`} title="Calendar"
        aria-haspopup="dialog" aria-expanded={open}
        className="rounded border border-slate-300 px-2 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        onClick={() => setOpen((o) => !o)}>
        <CalendarIcon />
      </button>
      {open && (
        <Calendar kind={kind} value={value} anchor={button} onPick={onPick} onClose={close} clearable={clearable} />
      )}
    </span>
  )
}

/** 날짜 칸(창 안) — 「yyyy-mm-dd」 글 + 달력. 값은 친 글 그대로 부르는 쪽에 준다(읽는 것은 부르는 쪽). */
export function DateField({ label, name, value, onChange }: {
  label: string
  /** 달력 단추 이름에 쓰는 짧은 칸 이름. */
  name: string
  value: string
  onChange: (text: string) => void
}) {
  const id = useId()
  return (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={id} className="font-medium">{label}</label>
      <div className="flex items-stretch gap-1">
        <input id={id} value={value} placeholder={DATE_FORMAT} maxLength={10} className={`${INPUT} min-w-0 flex-1`}
          onChange={(e) => onChange(e.target.value)} />
        <CalendarButton kind="date" name={name} value={value} onPick={onChange} />
      </div>
    </div>
  )
}

/** 시각 칸(창 안) — 「yyyy-mm-dd HH:mm:ss」 글 + 달력(날짜와 시·분·초). */
export function DateTimeField({ label, name, value, onChange }: {
  label: string
  name: string
  value: string
  onChange: (text: string) => void
}) {
  const id = useId()
  return (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={id} className="font-medium">{label}</label>
      <div className="flex items-stretch gap-1">
        <input id={id} value={value} placeholder={DATE_TIME_FORMAT} maxLength={19} className={`${INPUT} min-w-0 flex-1`}
          onChange={(e) => onChange(e.target.value)} />
        <CalendarButton kind="datetime" name={name} value={value} onPick={onChange} />
      </div>
    </div>
  )
}
